'use client';

import { useQuery } from '@tanstack/react-query';

const PRICE_STALE_TIME = 120_000; // 2 minutes
const PRICE_REFETCH_INTERVAL = 300_000; // 5 minutes

type PriceMap = Record<string, number>;

async function fetchTokenPrices(addresses: string[]): Promise<PriceMap> {
  if (addresses.length === 0) return {};
  try {
    const res = await fetch(
      `/api/rpc/token-prices?addresses=${addresses.join(',')}&intent=display`,
    );
    if (!res.ok) return {};
    const json = await res.json();
    return (json.data ?? {}) as PriceMap;
  } catch (err) {
    console.error('[useTokenPrices] Failed to fetch:', err);
    return {};
  }
}

/**
 * Fetch USD prices for a set of token addresses (Base chain).
 * The server caches CoinGecko responses for 5 min, so all clients
 * share one upstream call per token batch.
 */
export function useTokenPrices(addresses: string[]) {
  const sorted = [...new Set(addresses.map((a) => a.toLowerCase()))].sort();
  const key = sorted.join(',');

  const { data: prices, isLoading, error } = useQuery({
    queryKey: ['token-prices', key],
    queryFn: () => fetchTokenPrices(sorted),
    staleTime: PRICE_STALE_TIME,
    refetchInterval: PRICE_REFETCH_INTERVAL,
    enabled: sorted.length > 0,
  });

  return { prices: prices ?? {}, isLoading, error };
}

const INTERACTIVE_STALE_TIME = 45_000;
const INTERACTIVE_REFETCH_INTERVAL = 90_000;

interface InteractiveQuoteRow {
  priceUsd: number;
  warning?: string;
}

/**
 * Fresh USD prices for transaction / pricing UX — uses `interactive` intent on the server
 * (short cache, no stale DB/registry fallback) and surfaces CoinGecko warnings.
 */
export function useTokenPricesInteractive(addresses: string[]) {
  const sorted = [...new Set(addresses.map((a) => a.toLowerCase()))].sort();
  const key = sorted.join(',');

  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['token-prices-interactive', key],
    queryFn: async (): Promise<{ prices: PriceMap; warning?: string }> => {
      if (sorted.length === 0) return { prices: {} };
      const res = await fetch('/api/prices/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ addresses: sorted, intent: 'interactive' }),
      });
      if (!res.ok) return { prices: {}, warning: 'Price request failed.' };
      const json = (await res.json()) as {
        quotes?: Record<string, InteractiveQuoteRow>;
      };
      const prices: PriceMap = {};
      let warning: string | undefined;
      const quotes = json.quotes ?? {};
      for (const a of sorted) {
        const q = quotes[a];
        if (q && q.priceUsd > 0) prices[a] = q.priceUsd;
        if (q?.warning) warning = q.warning;
      }
      return { prices, warning };
    },
    staleTime: INTERACTIVE_STALE_TIME,
    refetchInterval: INTERACTIVE_REFETCH_INTERVAL,
    enabled: sorted.length > 0,
  });

  return {
    prices: data?.prices ?? {},
    warning: data?.warning,
    isLoading,
    error,
  };
}

const ZERO_ADDR = '0x0000000000000000000000000000000000000000';
const WETH_ADDR = '0x4200000000000000000000000000000000000006';

/**
 * Convert a raw token amount to USD using the token's decimals and price.
 */
export function tokenToUsd(
  rawAmount: bigint,
  decimals: number,
  priceUsd: number,
): number {
  const humanAmount = Number(rawAmount) / 10 ** decimals;
  return humanAmount * priceUsd;
}

/**
 * Get the effective token address for price lookup.
 * address(0) maps to WETH since they share the same price.
 */
export function effectiveTokenAddress(yieldToken: string | undefined): string {
  if (!yieldToken || yieldToken === ZERO_ADDR) return WETH_ADDR;
  return yieldToken.toLowerCase();
}
